import {
  LineChart as LineChartComponent,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useState } from "react";
import { Radio, Space } from "antd";
import "./lineChart.css";
import CustomTooltip from "./customToolTip/CustomToolTip";
import useScreenHook from "../../../utils/helperFunctions/useScreenHook";
import colorArray from "../../../utils/JSON/colorArray";
import NoDataComponent from "../NoDataComponent";

interface ChartSetting {
  dataKey: string;
  lineColor: string;
}

type AccentColorObject = {
  dataKey: string;
  lineColor: string;
};

type LineChartProps = {
  chartSetting: Array<ChartSetting>;
  data: any;
};

const CustomLegend = ({
  payload,
  setActiveLegend,
  activeLegend,
  chartSetting,
  styling,
  deviceType,
}: any) => {
  const handleLegendChange = ({ target }: any) => {
    setActiveLegend(target?.value);
  };

  if (payload && payload.length) {
    return (
      <div
        className={`custom-legend w-full flex mb-4 ${deviceType === "desktop" ? "" : "ml-6"} `}
      >
        <Radio.Group
          className="w-full"
          onChange={handleLegendChange}
          defaultValue={activeLegend}
          value={activeLegend}
        >
          <Space
            direction="horizontal"
            className="w-full justify-around"
            style={deviceType === "desktop" ? styling?.radioGroupSpace : null}
          >
            {payload?.map((legend: any, index: number) => (
              <div
                key={index + legend.value}
                className="flex gap-3 cursor-pointer items-center"
              >
                <Radio
                  checked={activeLegend === legend.value ? true : false}
                  value={legend.value}
                  className={`customAntdRadio color-${legend.color.replace("#", "")}`}
                >
                  <span
                    className={`capitalize cursor-pointer font-semibold font-salara md:text-sm text-nm`}
                  >{`${chartSetting[index].label}`}</span>
                </Radio>
              </div>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );
  }

  return null;
};

const CustomizedLabel = ({ x, y, stroke, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={5}
        textAnchor="middle"
        fill="#666"
        className="md:text-[16px] text-[9px]"
      >
        {payload.value}
      </text>
    </g>
  );
};

export default function LineChart(props: any) {
  const { rawData } = props;
  const {
    data,
    chartSetting,
    defaultLegend = "",
    showLegend = true,
    customeStyle = {},
    unit = "Articles",
  } = rawData;
  const [activeLegend, setActiveLegend] = useState(defaultLegend);
  const { deviceType = "desktop" } = useScreenHook();

  if (!data || !data.length) {
    return (
      <div className="h-[80%] pr-1.5 w-full flex justify-center">
        <NoDataComponent />
      </div>
    );
  }
  return (
    <div className="h-[83%] pr-1.5 w-full flex justify-center">
      <ResponsiveContainer width="100%" height="100%">
        <LineChartComponent
          data={data}
          margin={{
            top: 5,
            left: deviceType === "desktop" ? 0 : -25,
            bottom: 45,
            right: 30,
          }}
        >
          <CartesianGrid
            vertical={false}
            horizontal={true}
            strokeWidth={1}
            stroke={"#EAEAEA"}
          />
          <XAxis
            dataKey="pubDate"
            allowDataOverflow
            axisLine={false}
            tick={true}
            angle={-75}
            tickLine={true}
            tickMargin={35}
            tickSize={8}
            padding={{ left: deviceType === "desktop" ? 10 : 0, right: 0 }}
            // tick={<CustomizedLabel/>}
            className="md:text-[16px] text-[9px]"
          />
          <YAxis
            axisLine={false}
            tick={true}
            tickLine={false}
            tickMargin={deviceType === "desktop" ? 8 : 0}
            // tick={<CustomizedLabel/>}
            className="md:text-[16px] text-[9px]"
          />
          <Tooltip
            content={<CustomTooltip unit={unit} activeLegend={activeLegend} />}
            cursor={false}
          />
          {showLegend && (
            <Legend
              verticalAlign="top"
              content={
                <CustomLegend
                  setActiveLegend={setActiveLegend}
                  activeLegend={activeLegend}
                  chartSetting={chartSetting}
                  styling={customeStyle?.legendStyle}
                  deviceType={deviceType}
                />
              }
            />
          )}
          {chartSetting?.map((item: any, index: number) => (
            <Line
              key={index + item.dataKey}
              unit="pubDate"
              type="bump"
              dataKey={item.dataKey}
              stroke={item.lineColor}
              legendType="circle"
              strokeWidth={item?.dataKey === activeLegend ? 5 : 3}
              // dot={{
              //   stroke: "white",
              //   strokeWidth: 1,
              //   r: item?.dataKey === activeLegend ? 5 : 0,
              //   fill: colorArray[6],
              // }}
              dot={false}
              activeDot={{
                r: item?.dataKey === activeLegend ? 7 : 0,
                fill: colorArray[6],
              }}
              opacity={item?.dataKey === activeLegend ? 1 : 0.2}
            />
          ))}
        </LineChartComponent>
      </ResponsiveContainer>
      <style>{`
      tspan{
      font-size: 11px;
      }
      `}</style>
    </div>
  );
}
